import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Search, Users, ShoppingCart, Package, AlertCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: 'user' | 'order' | 'product' | 'dispute';
  data?: any;
}

interface GlobalSearchProps {
  onNavigate?: (tab: string, itemId?: string) => void;
}

export const GlobalSearch = ({ onNavigate }: GlobalSearchProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Raccourci clavier Ctrl+K ou Cmd+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    setLoading(true);
    const searchResults: SearchResult[] = [];

    try {
      const lowerQuery = searchQuery.toLowerCase().trim();

      // Recherche dans les utilisateurs
      const { data: users } = await supabase
        .from('profiles')
        .select('id, full_name, system_role, business_name')
        .or(`full_name.ilike.%${lowerQuery}%,business_name.ilike.%${lowerQuery}%`)
        .limit(5);

      if (users) {
        users.forEach(user => {
          searchResults.push({
            id: user.id,
            title: user.full_name || 'Sans nom',
            subtitle: `${user.role}${user.business_name ? ` • ${user.business_name}` : ''}`,
            type: 'user',
            data: user
          });
        });
      }

      // Recherche dans les commandes
      const { data: orders } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          status,
          amount_cents,
          currency,
          client:profiles!orders_client_id_fkey(full_name),
          product:products(title)
        `)
        .or(`order_number.ilike.%${lowerQuery}%,id::text.ilike.%${lowerQuery}%`)
        .limit(5);

      if (orders) {
        orders.forEach((order: any) => {
          searchResults.push({
            id: order.id,
            title: `Commande ${order.order_number || order.id.slice(0, 8)}`,
            subtitle: `${order.product?.title || 'Produit inconnu'} • ${order.client?.full_name || 'Client inconnu'} • ${order.status}`,
            type: 'order',
            data: order
          });
        });
      }

      // Recherche dans les produits
      const { data: products } = await supabase
        .from('products')
        .select(`
          id,
          title,
          price_cents,
          currency,
          stock,
          vendor:profiles!products_vendor_id_fkey(full_name, business_name)
        `)
        .ilike('title', `%${lowerQuery}%`)
        .limit(5);

      if (products) {
        products.forEach((product: any) => {
          searchResults.push({
            id: product.id,
            title: product.title,
            subtitle: `${product.vendor?.business_name || product.vendor?.full_name || 'Vendeur inconnu'} • ${(product.price_cents / 100).toFixed(2)} ${product.currency} • Stock: ${product.stock}`,
            type: 'product',
            data: product
          });
        });
      }

      // Recherche dans les réclamations
      const { data: disputes } = await supabase
        .from('order_disputes')
        .select(`
          id,
          reason,
          status,
          created_at,
          order:orders(order_number),
          user:profiles!order_disputes_user_id_fkey(full_name)
        `)
        .or(`reason.ilike.%${lowerQuery}%,description.ilike.%${lowerQuery}%`)
        .limit(5);

      if (disputes) {
        disputes.forEach((dispute: any) => {
          searchResults.push({
            id: dispute.id,
            title: dispute.reason,
            subtitle: `${dispute.user?.full_name || 'Utilisateur inconnu'} • Commande ${dispute.order?.order_number || 'N/A'} • ${dispute.status}`,
            type: 'dispute',
            data: dispute
          });
        });
      }

      setResults(searchResults);
    } catch (error) {
      console.error('Erreur de recherche:', error);
      toast({
        title: 'Erreur de recherche',
        description: 'Une erreur est survenue lors de la recherche',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (result: SearchResult) => {
    setOpen(false);
    setQuery('');

    switch (result.type) {
      case 'user':
        if (onNavigate) {
          onNavigate('personnel');
        }
        break;
      case 'order':
        navigate(`/admin/order/${result.id}`);
        break;
      case 'product':
        navigate(`/product/${result.id}`);
        break;
      case 'dispute':
        if (onNavigate) {
          onNavigate('disputes');
        }
        break;
    }
  };

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'user':
        return <Users className="h-4 w-4" />;
      case 'order':
        return <ShoppingCart className="h-4 w-4" />;
      case 'product':
        return <Package className="h-4 w-4" />;
      case 'dispute':
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getGroupTitle = (type: SearchResult['type']) => {
    switch (type) {
      case 'user':
        return 'Utilisateurs';
      case 'order':
        return 'Commandes';
      case 'product':
        return 'Produits';
      case 'dispute':
        return 'Réclamations';
    }
  };

  // Grouper les résultats par type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  return (
    <>
      <Button
        variant="outline"
        className="relative w-full justify-start text-sm text-muted-foreground sm:pr-12 md:w-64"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="hidden sm:inline-flex">Rechercher...</span>
        <span className="inline-flex sm:hidden">Recherche</span>
        <kbd className="pointer-events-none absolute right-1.5 top-2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Rechercher des utilisateurs, commandes, produits..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <CommandEmpty>Aucun résultat trouvé.</CommandEmpty>
          )}

          {!loading && results.length > 0 && (
            <>
              {Object.entries(groupedResults).map(([type, groupResults]) => (
                <CommandGroup
                  key={type}
                  heading={getGroupTitle(type as SearchResult['type'])}
                >
                  {groupResults.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={`${result.type}-${result.id}`}
                      onSelect={() => handleSelect(result)}
                      className="cursor-pointer"
                    >
                      {getIcon(result.type)}
                      <div className="ml-2 flex flex-col">
                        <span className="font-medium">{result.title}</span>
                        {result.subtitle && (
                          <span className="text-xs text-muted-foreground">
                            {result.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </>
          )}

          {!loading && query.length > 0 && query.length < 2 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Tapez au moins 2 caractères pour rechercher
            </div>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};
